/**
 * Control-flow snippets for the snippet library — `{% if %}` conditionals and
 * `{% for %}` loops. Unlike field snippets (a single `{{ ... }}` substitution
 * modelled as an `AnnotationDraft`), a control-flow snippet is a whole
 * multi-line block: an opening tag, optional middle branches, and a closing
 * tag. They're session-only reference syntax — users build one, read the
 * structure, and copy it. Nothing here ever hits the backend.
 */

export type ControlFlowKind = 'conditional' | 'loop';

/**
 * What the block applies to, controlling the docxtpl directive prefix:
 *   - 'inline'     → `{% … %}` stays within the line.
 *   - 'paragraph'  → `{%p … %}` consumes the whole paragraph (tag lines are
 *                    removed at render, so no stray blank lines).
 * Optional + defaults to inline so existing callers (web) are unaffected.
 */
export type ControlFlowScope = 'inline' | 'paragraph';

/**
 * One branch of a conditional. The first branch in a snippet is always the
 * `if`; every branch after it renders as an `elif`.
 */
export interface ConditionalBranch {
  /** Stable client-side id for v-for keys. */
  local_id: string;
  /** The inner expression, e.g. `is_vip_client` or `total > 1000`. */
  expression: string;
}

export interface ControlFlowSnippet {
  /** Stable client-side id for v-for keys / edit wiring. */
  local_id: string;
  kind: ControlFlowKind;
  /** Conditional only — ordered branches. `branches[0]` is the `if`. */
  branches: ConditionalBranch[];
  /** Conditional only — whether a trailing `{% else %}` branch is included. */
  hasElse: boolean;
  /** Loop only — the loop variable (left of `in`), e.g. `service`. */
  loopVar: string;
  /** Loop only — the iterable (right of `in`), e.g. `services`. */
  iterable: string;
  /** What the block wraps. Omitted/`inline` keeps plain `{% … %}`. */
  scope?: ControlFlowScope;
}

/**
 * Map a scope to the docxtpl tag-prefix token: 'inline' → '', 'paragraph' →
 * 'p'. The token sits right after `{%` (e.g. `{%p for … %}`).
 */
function scopeToken(scope: ControlFlowScope | undefined): string {
  return scope === 'paragraph' ? 'p' : '';
}

let cfSeq = 0;
function nextId(prefix: string): string {
  cfSeq += 1;
  return `${prefix}-${Date.now().toString(36)}-${cfSeq.toString(36)}`;
}

export function makeControlFlowLocalId(): string {
  return nextId('cf');
}

export function makeBranchLocalId(): string {
  return nextId('branch');
}

/** A fresh conditional draft with a single empty `if` branch. */
export function newConditionalSnippet(): ControlFlowSnippet {
  return {
    local_id: makeControlFlowLocalId(),
    kind: 'conditional',
    branches: [{ local_id: makeBranchLocalId(), expression: '' }],
    hasElse: false,
    loopVar: '',
    iterable: '',
  };
}

/** A fresh loop draft. */
export function newLoopSnippet(): ControlFlowSnippet {
  return {
    local_id: makeControlFlowLocalId(),
    kind: 'loop',
    branches: [],
    hasElse: false,
    loopVar: '',
    iterable: '',
  };
}

/**
 * Render a control-flow snippet to its full multi-line Jinja block. Empty
 * expressions fall back to readable placeholders so the copied syntax always
 * parses and reads as an example. Tags are separated by a blank line so the
 * structure is obvious and there's room to paste content between them.
 */
export function renderControlFlowSnippet(snippet: ControlFlowSnippet): string {
  const tok = scopeToken(snippet.scope);
  if (snippet.kind === 'loop') {
    const loopVar = snippet.loopVar.trim() || 'item';
    const iterable = snippet.iterable.trim() || 'items';
    return [`{%${tok} for ${loopVar} in ${iterable} %}`, '', `{%${tok} endfor %}`].join('\n');
  }

  const lines: string[] = [];
  for (const [index, branch] of snippet.branches.entries()) {
    const keyword = index === 0 ? 'if' : 'elif';
    const fallback = index === 0 ? 'condition' : 'other_condition';
    const expr = branch.expression.trim() || fallback;
    lines.push(`{%${tok} ${keyword} ${expr} %}`, '');
  }
  if (snippet.hasElse) {
    lines.push(`{%${tok} else %}`, '');
  }
  lines.push(`{%${tok} endif %}`);
  return lines.join('\n');
}

/**
 * Split a control-flow snippet into the parts needed to WRAP an existing
 * block of content: `prefix` goes before the block, `suffix` after it. The
 * wrapped content becomes the body of the first branch (the `if`) or the
 * loop; any further `elif` / `else` branches render after the block with
 * empty bodies for the user to fill in.
 */
export function renderControlFlowWrapParts(snippet: ControlFlowSnippet): {
  prefix: string;
  suffix: string;
} {
  const tok = scopeToken(snippet.scope);
  if (snippet.kind === 'loop') {
    const loopVar = snippet.loopVar.trim() || 'item';
    const iterable = snippet.iterable.trim() || 'items';
    return { prefix: `{%${tok} for ${loopVar} in ${iterable} %}`, suffix: `{%${tok} endfor %}` };
  }

  const firstExpression = snippet.branches[0]?.expression.trim() || 'condition';
  const suffixLines: string[] = [];
  for (const branch of snippet.branches.slice(1)) {
    suffixLines.push(`{%${tok} elif ${branch.expression.trim() || 'other_condition'} %}`, '');
  }
  if (snippet.hasElse) {
    suffixLines.push(`{%${tok} else %}`, '');
  }
  suffixLines.push(`{%${tok} endif %}`);
  return { prefix: `{%${tok} if ${firstExpression} %}`, suffix: suffixLines.join('\n') };
}

/** Short uppercase badge shown on the snippet card. */
export function controlFlowKindLabel(snippet: ControlFlowSnippet): string {
  return snippet.kind === 'loop' ? 'Loop' : 'Condition';
}

/** Monospace one-line summary shown next to the badge on the card. */
export function controlFlowSubLabel(snippet: ControlFlowSnippet): string {
  if (snippet.kind === 'loop') {
    const loopVar = snippet.loopVar.trim() || 'item';
    const iterable = snippet.iterable.trim() || 'items';
    return `for ${loopVar} in ${iterable}`;
  }
  const parts: string[] = [];
  for (const [index, branch] of snippet.branches.entries()) {
    const keyword = index === 0 ? 'if' : 'elif';
    const expr = branch.expression.trim() || (index === 0 ? 'condition' : '…');
    parts.push(`${keyword} ${expr}`);
  }
  if (snippet.hasElse) parts.push('else');
  return parts.join(' · ');
}
